/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PortType, toSeconds, formatDuration } from "./timeControls";

// Extended interval literal type to include days for large ranges
export type ExtendedIntervalLiteral = 
  | "5s" | "10s" | "15s" | "30s"
  | "1m" | "2m" | "5m" | "10m" | "15m" | "30m"
  | "1h" | "2h" | "4h" | "6h" | "8h" | "12h" | "24h"
  | "2d" | "3d" | "4d" | "7d";

// ===== Constants =====
export const MAX_RANGE_DAYS = 90;
// TEMPORAL: Para puertos virtuales, el límite máximo hacia atrás es 7 días (en lugar de 90)
export const VIRTUAL_PORT_MAX_RANGE_DAYS = 7;
export const MIN_RANGE_DAYS = 2; // Mínimo de días permitidos en el rango
export const TARGET_POINTS = 180; // default maxPoints
export const HARD_MAX_POINTS = 800; // guard rail para evitar requests pesados

// Interval ladder segura (ordenada de menor a mayor)
export const INTERVAL_LADDER: readonly ExtendedIntervalLiteral[] = [
  "5s", "10s", "15s", "30s",
  "1m", "2m", "5m", "10m", "15m", "30m",
  "1h", "2h", "4h", "6h", "8h", "12h", "24h",
  "2d", "3d", "4d", "7d"
] as const;

// ===== Type Definitions =====
export interface DateRangeValidation {
  isValid: boolean;
  error?: string;
}

export interface IntervalBounds {
  minSeconds: number;
  maxSeconds: number;
}

export interface IntervalOption {
  value: ExtendedIntervalLiteral;
  label: string;
  level: "high" | "medium" | "low";
}

// ===== Date Utilities =====

/**
 * Convierte ISO string a milisegundos
 */
export function parseISOtoMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  try {
    return new Date(iso).getTime();
  } catch {
    return null;
  }
}

/**
 * Calcula la diferencia en días calendario entre dos fechas (inclusivos)
 * Ambos días se cuentan, por lo que 28 nov a 26 feb = 90 días calendario
 * Usa cálculo directo de componentes de fecha para evitar problemas de zona horaria
 * y errores de precisión con milisegundos
 */
function getCalendarDays(start: Date, end: Date): number {
  // Normalizar ambas fechas a medianoche en hora local
  const d1 = new Date(start);
  d1.setHours(0, 0, 0, 0);
  const d2 = new Date(end);
  d2.setHours(0, 0, 0, 0);
  
  // Obtener componentes de fecha en hora local (no UTC)
  const year1 = d1.getFullYear();
  const month1 = d1.getMonth();
  const day1 = d1.getDate();
  
  const year2 = d2.getFullYear();
  const month2 = d2.getMonth();
  const day2 = d2.getDate();
  
  // Calcular diferencia usando componentes de fecha directamente
  // Crear fechas en hora local (no UTC) para mantener consistencia
  const date1Local = new Date(year1, month1, day1);
  const date2Local = new Date(year2, month2, day2);
  
  // Calcular diferencia en milisegundos
  const diffMs = Math.abs(date2Local.getTime() - date1Local.getTime());
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  
  // Usar Math.floor para días completos y agregar 1 para contar ambos días como inclusivos
  // No agregar tolerancia aquí porque estamos usando fechas normalizadas en la misma zona horaria
  return Math.floor(diffDays) + 1;
}

/**
 * Normaliza y valida un rango de fechas según las reglas de negocio.
 * Ajusta automáticamente si excede límites.
 * Retorna null si el rango es inválido después de intentar ajustar.
 * 
 * Esta es la función única (single source of truth) para validación y clamp.
 * Reemplaza a clampRangeTo90Days y validateDateRange.
 * 
 * TEMPORAL: Para puertos virtuales, usa VIRTUAL_PORT_MAX_RANGE_DAYS (8 días) en lugar de MAX_RANGE_DAYS (90 días).
 */
export function normalizeDateRange(
  start: Date | null,
  end: Date | null,
  portType?: PortType
): { start: Date; end: Date } | null {
  if (!start || !end) return null;

  const now = new Date();
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);
  
  // TEMPORAL: Para puertos virtuales, usar 8 días en lugar de 90
  const maxRangeDays = portType === "virtual" ? VIRTUAL_PORT_MAX_RANGE_DAYS : MAX_RANGE_DAYS;
  
  const maxPast = new Date(now);
  maxPast.setDate(maxPast.getDate() - maxRangeDays);
  maxPast.setHours(0, 0, 0, 0);

  // Normalizar a medianoche
  let normalizedStart = new Date(start);
  normalizedStart.setHours(0, 0, 0, 0);
  let normalizedEnd = new Date(end);
  normalizedEnd.setHours(0, 0, 0, 0);

  // Clamp a límites temporales
  if (normalizedStart > endOfToday) normalizedStart = endOfToday;
  if (normalizedEnd > endOfToday) normalizedEnd = endOfToday;
  if (normalizedStart < maxPast) normalizedStart = maxPast;

  // Asegurar end >= start
  if (normalizedEnd < normalizedStart) {
    normalizedEnd = normalizedStart;
  }

  // Validar longitud del rango
  const calendarDays = getCalendarDays(normalizedStart, normalizedEnd);
  
  if (calendarDays < MIN_RANGE_DAYS) {
    // Ajustar a mínimo 2 días
    normalizedEnd = new Date(normalizedStart);
    normalizedEnd.setDate(normalizedEnd.getDate() + 1);
    normalizedEnd.setHours(0, 0, 0, 0);
    // Asegurar que end no exceda endOfToday
    if (normalizedEnd > endOfToday) {
      normalizedEnd = endOfToday;
      // Si end está limitado, ajustar start hacia atrás si es posible
      const adjustedStart = new Date(normalizedEnd);
      adjustedStart.setDate(adjustedStart.getDate() - 1);
      adjustedStart.setHours(0, 0, 0, 0);
      if (adjustedStart >= maxPast) {
        normalizedStart = adjustedStart;
      }
    }
  } else if (calendarDays > maxRangeDays) {
    // Ajustar a máximo (90 días para normal, 8 días para virtual)
    normalizedStart = new Date(normalizedEnd);
    normalizedStart.setDate(normalizedStart.getDate() - (maxRangeDays - 1));
    normalizedStart.setHours(0, 0, 0, 0);
    // Asegurar que start no sea menor que maxPast
    if (normalizedStart < maxPast) {
      normalizedStart = maxPast;
      // Si start está limitado, ajustar end hacia adelante si es posible
      const adjustedEnd = new Date(normalizedStart);
      adjustedEnd.setDate(adjustedEnd.getDate() + (maxRangeDays - 1));
      adjustedEnd.setHours(0, 0, 0, 0);
      if (adjustedEnd <= endOfToday) {
        normalizedEnd = adjustedEnd;
      }
    }
  }

  // Validación final
  const finalDays = getCalendarDays(normalizedStart, normalizedEnd);
  if (finalDays < MIN_RANGE_DAYS || finalDays > maxRangeDays) {
    return null;
  }

  return { start: normalizedStart, end: normalizedEnd };
}

/**
 * @deprecated Usar normalizeDateRange en su lugar. Esta función se mantiene por compatibilidad.
 * Asegura que el rango no exceda 90 días hacia el pasado
 * end <= today, start >= today-90d, y end-start <= 90d
 * 
 * IMPORTANTE: Para rangos de fechas con dateOnly=true, el cálculo debe considerar
 * que ambos días (start y end) se incluyen, por lo que el máximo permitido
 * es exactamente 90 días calendario inclusivos.
 * 
 * TEMPORAL: Para puertos virtuales, usa VIRTUAL_PORT_MAX_RANGE_DAYS (8 días).
 */
export function clampRangeTo90Days(
  start: Date | null,
  end: Date | null,
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  dateOnly: boolean = false,
  portType?: PortType
): { start: Date | null; end: Date | null } {
  const normalized = normalizeDateRange(start, end, portType);
  return normalized || { start, end };
}

/**
 * Calcula la duración del rango en segundos usando días calendario inclusivos.
 * 
 * IMPORTANTE: Para dateOnly=true, usa días calendario inclusivos.
 * Ejemplo: 01/02/2026 - 26/02/2026 = 26 días calendario = (26-1) * 86400 = 2,160,000 segundos
 */
export function computeRangeSeconds(start: Date | null, end: Date | null): number {
  if (!start || !end) return 0;
  
  // Usar días calendario inclusivos para mayor precisión
  const calendarDays = getCalendarDays(start, end);
  // Días inclusivos: si tengo 26 días calendario, el rango en segundos es (26-1) * 86400
  // porque el primer día cuenta como día 0 en términos de duración
  return (calendarDays - 1) * 86400;
}

/**
 * Convierte un rango de fechas a ISO UTC para el backend.
 * startDate: 00:00:00 en America/Mexico_City → ISO UTC
 * endDate: 23:59:59.999 en America/Mexico_City → ISO UTC
 * 
 * Usa un método simple: obtener componentes de fecha en America/Mexico_City,
 * construir fecha ISO string, y usar el offset calculado para convertir a UTC.
 */
export function dateRangeToISOUTC(start: Date, end: Date): { startISO: string; endISO: string } {
  const tz = 'America/Mexico_City';
  
  // Helper: convertir fecha a UTC con hora específica en America/Mexico_City
  const dateToUTC = (date: Date, hour: number, minute: number, second: number, millisecond: number): Date => {
    // Obtener componentes de fecha en America/Mexico_City
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    
    const parts = formatter.formatToParts(date);
    const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
    const month = parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1; // 0-indexed
    const day = parseInt(parts.find(p => p.type === 'day')?.value || '0');
    
    // Construir fecha ISO string (asumiendo que es en UTC primero)
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}.${String(millisecond).padStart(3, '0')}`;
    
    // Crear fecha temporal en UTC
    const tempUTC = new Date(`${dateStr}Z`);
    
    // Obtener el offset de America/Mexico_City para esta fecha
    // Usar Intl para obtener la diferencia entre cómo se ve esta fecha en UTC vs en tz
    const utcTime = tempUTC.getTime();
    
    // Obtener cómo se ve esta fecha en America/Mexico_City
    const tzFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    
    const tzParts = tzFormatter.formatToParts(tempUTC);
    const tzYear = parseInt(tzParts.find(p => p.type === 'year')?.value || '0');
    const tzMonth = parseInt(tzParts.find(p => p.type === 'month')?.value || '0') - 1;
    const tzDay = parseInt(tzParts.find(p => p.type === 'day')?.value || '0');
    const tzHour = parseInt(tzParts.find(p => p.type === 'hour')?.value || '0');
    const tzMinute = parseInt(tzParts.find(p => p.type === 'minute')?.value || '0');
    const tzSecond = parseInt(tzParts.find(p => p.type === 'second')?.value || '0');
    
    // Crear fecha local que representa la misma fecha/hora en tz
    const tzLocal = new Date(tzYear, tzMonth, tzDay, tzHour, tzMinute, tzSecond, millisecond);
    
    // Calcular offset: diferencia entre UTC y tz para esta fecha
    const offsetMs = utcTime - tzLocal.getTime();
    
    // Crear fecha local con los componentes deseados
    const desiredLocal = new Date(year, month, day, hour, minute, second, millisecond);
    
    // Ajustar por el offset para obtener UTC equivalente
    return new Date(desiredLocal.getTime() + offsetMs);
  };
  
  // Para startDate: 00:00:00 en America/Mexico_City
  const startUTC = dateToUTC(start, 0, 0, 0, 0);
  
  // Para endDate: 23:59:59.999 en America/Mexico_City
  const endUTC = dateToUTC(end, 23, 59, 59, 999);
  
  return {
    startISO: startUTC.toISOString(),
    endISO: endUTC.toISOString(),
  };
}

/**
 * @deprecated Usar normalizeDateRange en su lugar. Esta función se mantiene por compatibilidad.
 * Valida un rango de fechas según las reglas de negocio
 * 
 * IMPORTANTE: Para rangos de fechas con dateOnly=true, el cálculo debe considerar
 * que ambos días (start y end) se incluyen, por lo que el máximo permitido
 * en milisegundos es 89 días completos (para resultar en 90 días calendario).
 * 
 * TEMPORAL: Para puertos virtuales, usa VIRTUAL_PORT_MAX_RANGE_DAYS (8 días).
 */
export function validateDateRange(
  start: Date | null,
  end: Date | null,
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  dateOnly: boolean = false,
  portType?: PortType
): DateRangeValidation {
  const normalized = normalizeDateRange(start, end, portType);
  if (!normalized) {
    return { isValid: false, error: "Invalid date range" };
  }
  return { isValid: true };
}

// ===== Interval Calculation =====

/**
 * Obtiene los bounds de intervalo según portType
 * Para rangos grandes (>= 60 días), permite intervalos mayores
 */
export function getIntervalBounds(portType?: PortType, rangeDays?: number): IntervalBounds {
  const isLargeRange = rangeDays !== undefined && rangeDays >= 60;
  
  if (portType === "virtual") {
    // TEMPORAL: Comentando la regla del máximo intervalo para puertos virtuales
    // Virtual: mantiene límite de 7h para rangos normales
    // return {
    //   minSeconds: toSeconds("5m"),
    //   maxSeconds: toSeconds("7h"),
    // };
    
    // TEMPORAL: Sin límite máximo para testing
    return {
      minSeconds: toSeconds("5m"),
      maxSeconds: Infinity, // Sin límite máximo temporalmente
    };
  }
  // physical
  if (isLargeRange) {
    // Para rangos grandes (>= 60 días), permitir hasta 7 días
    return {
      minSeconds: toSeconds("5s"),
      maxSeconds: toSeconds("7d"),
    };
  }
  // Para rangos normales, mantener límite de 24h
  return {
    minSeconds: toSeconds("5s"),
    maxSeconds: toSeconds("24h"),
  };
}

/**
 * Selecciona el intervalo ideal para un rango dado
 * Basado en TARGET_POINTS y respetando bounds y ladder
 */
export function pickIntervalForRange(
  rangeSeconds: number,
  portType?: PortType,
  maxPoints: number = TARGET_POINTS
): ExtendedIntervalLiteral | null {
  if (rangeSeconds <= 0) return null;

  const rangeDays = rangeSeconds / (24 * 60 * 60);
  const bounds = getIntervalBounds(portType, rangeDays);
  const idealIntervalSeconds = rangeSeconds / maxPoints;
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.debug('[pickIntervalForRange]', {
      rangeSeconds,
      rangeDays: rangeDays.toFixed(2),
      portType,
      idealIntervalSeconds,
      idealIntervalHours: (idealIntervalSeconds / 3600).toFixed(2),
      bounds: {
        minSeconds: bounds.minSeconds,
        maxSeconds: bounds.maxSeconds === Infinity ? 'Infinity' : bounds.maxSeconds,
        maxHours: bounds.maxSeconds === Infinity ? 'Infinity' : (bounds.maxSeconds / 3600).toFixed(2),
      },
    });
  }

  // Encontrar el menor intervalo en la ladder que:
  // 1. Sea >= idealIntervalSeconds
  // 2. Esté dentro de bounds
  // 3. Sea <= rangeSeconds
  let selected: ExtendedIntervalLiteral | null = null;

  for (const interval of INTERVAL_LADDER) {
    // Handle extended intervals (days) that might not be in timeControls
    const intervalSeconds = interval.includes('d') 
      ? parseInt(interval) * 24 * 3600 
      : toSeconds(interval as any);
    
    if (
      intervalSeconds >= idealIntervalSeconds &&
      intervalSeconds >= bounds.minSeconds &&
      intervalSeconds <= bounds.maxSeconds &&
      intervalSeconds <= rangeSeconds
    ) {
      selected = interval;
      break;
    }
  }

  // Si no encontramos uno >= ideal, tomar el mayor disponible dentro de bounds
  if (!selected) {
    for (let i = INTERVAL_LADDER.length - 1; i >= 0; i--) {
      const interval = INTERVAL_LADDER[i];
      // Handle extended intervals (days) that might not be in timeControls
      const intervalSeconds = interval.includes('d') 
        ? parseInt(interval) * 24 * 3600 
        : toSeconds(interval as any);
      
      if (
        intervalSeconds >= bounds.minSeconds &&
        intervalSeconds <= bounds.maxSeconds &&
        intervalSeconds <= rangeSeconds
      ) {
        selected = interval;
        break;
      }
    }
  }

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.debug('[pickIntervalForRange] Result:', {
      selected,
      selectedSeconds: selected ? (selected.includes('d') ? parseInt(selected) * 24 * 3600 : toSeconds(selected as any)) : null,
      selectedHours: selected ? ((selected.includes('d') ? parseInt(selected) * 24 * 3600 : toSeconds(selected as any)) / 3600).toFixed(2) : null,
    });
  }

  return selected;
}

/**
 * Genera 3 opciones de intervalo (High/Medium/Low) alrededor del recomendado
 */
export function getIntervalOptionsForRange(
  rangeSeconds: number,
  portType?: PortType,
  maxPoints: number = TARGET_POINTS
): IntervalOption[] {
  const recommended = pickIntervalForRange(rangeSeconds, portType, maxPoints);
  if (!recommended) return [];

  const rangeDays = rangeSeconds / (24 * 60 * 60);
  const bounds = getIntervalBounds(portType, rangeDays);
  const recommendedIdx = INTERVAL_LADDER.findIndex(i => i === recommended);
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.debug('[getIntervalOptionsForRange]', {
      recommended,
      recommendedIdx,
      bounds: {
        maxSeconds: bounds.maxSeconds === Infinity ? 'Infinity' : bounds.maxSeconds,
        maxHours: bounds.maxSeconds === Infinity ? 'Infinity' : (bounds.maxSeconds / 3600).toFixed(2),
      },
    });
  }

  // Helper to format extended intervals (days)
  const formatExtendedInterval = (interval: ExtendedIntervalLiteral): string => {
    if (interval.includes('d')) {
      const days = parseInt(interval);
      return days === 1 ? "1 Day" : `${days} Days`;
    }
    return formatDuration(interval as any);
  };

  const options: IntervalOption[] = [
    {
      value: recommended,
      label: formatExtendedInterval(recommended),
      level: "high",
    },
  ];

  // Medium: siguiente más grande en ladder (o el mismo si no hay)
  if (recommendedIdx < INTERVAL_LADDER.length - 1) {
    const medium = INTERVAL_LADDER[recommendedIdx + 1];
    // Handle extended intervals (days) that might not be in timeControls
    const mediumSeconds = medium.includes('d') 
      ? parseInt(medium) * 24 * 3600 
      : toSeconds(medium as any);
    if (
      mediumSeconds >= bounds.minSeconds &&
      mediumSeconds <= bounds.maxSeconds &&
      mediumSeconds <= rangeSeconds
    ) {
      options.push({
        value: medium,
        label: formatExtendedInterval(medium),
        level: "medium",
      });
    } else {
      // Si medium no es válido, usar recommended
      options.push({
        value: recommended,
        label: formatExtendedInterval(recommended),
        level: "medium",
      });
    }
  } else {
    options.push({
      value: recommended,
      label: formatExtendedInterval(recommended),
      level: "medium",
    });
  }

  // Low: otro más grande en ladder (o el último disponible)
  if (recommendedIdx < INTERVAL_LADDER.length - 2) {
    const low = INTERVAL_LADDER[recommendedIdx + 2];
    // Handle extended intervals (days) that might not be in timeControls
    const lowSeconds = low.includes('d') 
      ? parseInt(low) * 24 * 3600 
      : toSeconds(low as any);
    if (
      lowSeconds >= bounds.minSeconds &&
      lowSeconds <= bounds.maxSeconds &&
      lowSeconds <= rangeSeconds
    ) {
      options.push({
        value: low,
        label: formatExtendedInterval(low),
        level: "low",
      });
    } else {
      // Usar el último disponible
      const lastValid = options[options.length - 1].value;
      options.push({
        value: lastValid,
        label: formatExtendedInterval(lastValid),
        level: "low",
      });
    }
  } else {
    // Usar el último disponible
    const lastValid = options[options.length - 1].value;
    options.push({
      value: lastValid,
      label: formatExtendedInterval(lastValid),
      level: "low",
    });
  }

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.debug('[getIntervalOptionsForRange] Final options:', {
      options: options.map(opt => ({ value: opt.value, label: opt.label, level: opt.level })),
    });
  }

  return options;
}

/**
 * Calcula el número de puntos que se generarían con un intervalo dado
 */
export function calculatePoints(rangeSeconds: number, interval: ExtendedIntervalLiteral): number {
  // Handle extended intervals (days) that might not be in timeControls
  const intervalSeconds = interval.includes('d') 
    ? parseInt(interval) * 24 * 3600 
    : toSeconds(interval as any);
  if (intervalSeconds <= 0) return 0;
  return Math.ceil(rangeSeconds / intervalSeconds);
}

/**
 * Valida si un intervalo es seguro para un rango dado
 */
export function isIntervalSafe(
  rangeSeconds: number,
  interval: ExtendedIntervalLiteral,
  portType?: PortType
): { safe: boolean; points: number; error?: string } {
  const rangeDays = rangeSeconds / (24 * 60 * 60);
  const bounds = getIntervalBounds(portType, rangeDays);
  // Handle extended intervals (days) that might not be in timeControls
  const intervalSeconds = interval.includes('d') 
    ? parseInt(interval) * 24 * 3600 
    : toSeconds(interval as any);
  const points = calculatePoints(rangeSeconds, interval);

  if (intervalSeconds < bounds.minSeconds) {
    const minInterval = portType === "virtual" ? "5m" : "5s";
    return { safe: false, points, error: `Interval too small (min: ${formatDuration(minInterval)})` };
  }

  if (intervalSeconds > bounds.maxSeconds) {
    const maxIntervalSeconds = bounds.maxSeconds;
    // Format max interval for error message
    let maxIntervalStr = "24h";
    if (maxIntervalSeconds >= 7 * 24 * 3600) maxIntervalStr = "7d";
    else if (maxIntervalSeconds >= 4 * 24 * 3600) maxIntervalStr = "4d";
    else if (maxIntervalSeconds >= 3 * 24 * 3600) maxIntervalStr = "3d";
    else if (maxIntervalSeconds >= 2 * 24 * 3600) maxIntervalStr = "2d";
    else if (maxIntervalSeconds >= 24 * 3600) maxIntervalStr = "24h";
    else if (maxIntervalSeconds >= 12 * 3600) maxIntervalStr = "12h";
    else if (maxIntervalSeconds >= 7 * 3600) maxIntervalStr = "7h";
    return { safe: false, points, error: `Interval too large (max: ${formatDuration(maxIntervalStr as any)})` };
  }

  if (intervalSeconds > rangeSeconds) {
    return { safe: false, points, error: "Interval cannot be larger than date range" };
  }

  if (points > HARD_MAX_POINTS) {
    return { safe: false, points, error: `Too many points (${points}), maximum allowed: ${HARD_MAX_POINTS}` };
  }

  return { safe: true, points };
}
